import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../foundation/altzero_base/base/components/ui/select';
import { ContractDrawer } from './ContractDrawer';
import { environmentalConfigService } from '../../services/contractService';
import { ENV_CERT_TYPES_BY_STATE } from '../../types/billValidationTypes';
import type { BvEnvironmentalConfig, BvEnvironmentalConfigFormData, BvRateMode } from '../../types/billValidationTypes';

type Props = { siteId: string; siteState?: string };

const EMPTY: BvEnvironmentalConfigFormData = { certificate_type: '', rate_mode: 'fixed', rate_unit: 'per_mwh' };

export function EnvironmentalConfigSection({ siteId, siteState }: Props) {
  const [configs, setConfigs] = useState<BvEnvironmentalConfig[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState<BvEnvironmentalConfigFormData>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await environmentalConfigService.listBySite(siteId);
    if (r.success) setConfigs(r.data ?? []);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const certTypes = siteState
    ? ENV_CERT_TYPES_BY_STATE[siteState] ?? ENV_CERT_TYPES_BY_STATE['ALL']
    : ENV_CERT_TYPES_BY_STATE['ALL'];

  const set = <K extends keyof BvEnvironmentalConfigFormData>(k: K, v: BvEnvironmentalConfigFormData[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.certificate_type) return;
    setSaving(true);
    const r = await environmentalConfigService.create(siteId, form);
    if (r.success) { await load(); setDrawerOpen(false); }
    setSaving(false);
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this environmental config?')) return;
    await environmentalConfigService.delete(id);
    await load();
  };

  const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-AU') : '—';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Environmental Config</h3>
        <Button size="sm" variant="outline" onClick={() => { setForm(EMPTY); setDrawerOpen(true); }}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </div>

      {configs.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">No environmental configs</p>
      ) : (
        <div className="space-y-2">
          {configs.map((c) => (
            <div key={c.id} className="border border-gray-200 rounded-lg px-3 py-2.5">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700">{c.certificate_type}</span>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {c.rate_mode} · {fmtDate(c.period_start)} → {fmtDate(c.period_end)}
                    {c.rate != null ? ` · ${c.rate} ${c.rate_unit ?? ''}` : ''}
                  </p>
                </div>
                <button onClick={() => remove(c.id)} className="p-1 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContractDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Add Environmental Config"
      >
        <div className="space-y-4">
          <div>
            <Label>Certificate Type *</Label>
            <Select value={form.certificate_type} onValueChange={(v) => set('certificate_type', v)}>
              <SelectTrigger><SelectValue placeholder="Select type…" /></SelectTrigger>
              <SelectContent>
                {certTypes.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {siteState && (
              <p className="text-xs text-gray-400 mt-1">Showing types for {siteState}</p>
            )}
          </div>
          <div>
            <Label>Rate Mode</Label>
            <Select value={form.rate_mode ?? 'fixed'} onValueChange={(v) => set('rate_mode', v as BvRateMode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed</SelectItem>
                <SelectItem value="variable">Variable</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Period Start</Label>
              <Input type="date" value={form.period_start ?? ''} onChange={(e) => set('period_start', e.target.value)} />
            </div>
            <div>
              <Label>Period End</Label>
              <Input type="date" value={form.period_end ?? ''} onChange={(e) => set('period_end', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rate</Label>
              <Input type="number" step="0.0001" value={form.rate ?? ''} onChange={(e) => set('rate', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
            </div>
            <div>
              <Label>Unit</Label>
              <Input value={form.rate_unit ?? 'per_mwh'} onChange={(e) => set('rate_unit', e.target.value)} />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button className="flex-1" onClick={save} disabled={saving || !form.certificate_type}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </ContractDrawer>
    </div>
  );
}
